import { Injectable } from '@angular/core';
import { List } from 'immutable';
import { Observable, of } from 'rxjs';
import {
  ACCEPT_VERSION_HEADER,
  AuthHttpService,
} from '../auth-data/auth-http.service';
import { ITopLevelKeys } from '../entity-data/entity-data.data';
import { EntityDataService } from '../entity-data/entity-data.service';
import { ErrorService } from '../error-reporting/error.service';
import { IResponseInfo } from '../http/http.data';
import { GlobalSearch } from '../search-data/global-search.model';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { LoggerService } from '../shared/services/logger/logger.service';
import { ExpenseDataBuilder } from './expense-data.builder';
import {
  EXPENSE_ACCEPT_VERSION,
  EXPENSE_FILTER_ACCEPT_VERSION,
  EXPENSE_MERCHANT_AUTOCOMPLETE_ACCEPT_VERSION,
  EXPENSE_SEARCH_ACCEPT_VERSION,
} from './expense-data.data';
import { ExpenseFilters } from './models/expense-filters.model';
import { catchError, map } from 'rxjs/operators';

@Injectable()
export class ExpenseDataService extends EntityDataService {
  protected inputHeaders: any = {
    [ACCEPT_VERSION_HEADER]: EXPENSE_ACCEPT_VERSION,
  };
  protected filtersHeaders: any = {
    [ACCEPT_VERSION_HEADER]: EXPENSE_FILTER_ACCEPT_VERSION,
  };
  protected searchHeaders: any = {
    [ACCEPT_VERSION_HEADER]: EXPENSE_SEARCH_ACCEPT_VERSION,
  };

  topLevelKeys: ITopLevelKeys = {
    getKey: 'expenses',
    postKey: 'created_expense',
    putKey: 'updated_expense',
    deletedKey: 'deleted_expenses',
    deleteSingleKey: 'deleted_expense',
    getSingleKey: 'expense',
    searchResultsKey: 'expense_results',
    globalSearchResultsKey: GlobalSearch.KEYS.EXPENSES,
  };

  constructor(
    public authHttpService: AuthHttpService,
    private _loggerService: LoggerService,
    errorService: ErrorService,
    expenseDataBuilder: ExpenseDataBuilder,
    settingsDataStore: SettingsDataStore
  ) {
    super(
      authHttpService,
      '/expenses',
      _loggerService,
      errorService,
      expenseDataBuilder,
      settingsDataStore
    );
  }

  getCategories(): Observable<List<string>> {
    return this.authHttpService.get(`${this.entitiesUrl}/categories`).pipe(
      map(({ body, ..._ }: IResponseInfo) =>
        List<string>(body['expense_categories'])
      ),
      catchError(() => of(List<string>([])))
    );
  }

  getFilters(): Observable<ExpenseFilters> {
    const url = `${this.entitiesUrl}/filters`;

    return this.authHttpService
      .get(url, null, this.filtersHeaders)
      .pipe(map(({ body, ..._ }: IResponseInfo) => new ExpenseFilters(body)));
  }

  autocompleteMerchants(query: string): Observable<List<string>> {
    return this.authHttpService
      .get(
        '/autocomplete/expenses/merchants',
        { query },
        {
          [ACCEPT_VERSION_HEADER]: EXPENSE_MERCHANT_AUTOCOMPLETE_ACCEPT_VERSION,
        }
      )
      .pipe(
        map(({ body, ..._ }: IResponseInfo) =>
          List<string>(body['expense_merchants'])
        )
      );
  }
}
